/**
 * Format a date as relative time (e.g., "5m ago", "2h ago", "3d ago").
 * Accepts ISO date string or timestamp number.
 */
export function formatRelativeTime(date: string | number): string {
  const timestamp = typeof date === 'string' ? new Date(date).getTime() : date;
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);

  if (mins < 1) {
    return 'just now';
  }

  if (mins < 60) {
    return `${mins}m ago`;
  }

  const hours = Math.floor(mins / 60);

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);

  return `${days}d ago`;
}

/**
 * Format milliseconds as human-readable duration.
 * - Under 1s: "500ms"
 * - Under 1m: "5.2s"
 * - Under 1h: "5m 30s"
 * - Over 1h: "2h 15m"
 *
 * Returns "-" for null/undefined values.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) {
    return '-';
  }

  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }

  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Format milliseconds with high precision for test results.
 * - Under 1s: "245ms"
 * - Over 1s: "1.23s"
 */
export function formatDurationPrecise(ms: number): string {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }

  return `${(ms / 1000).toFixed(2)}s`;
}
